import { NavigationService } from './navigation.service';
import { Observable, throwError, of, forkJoin, Subject, firstValueFrom } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ApiResult } from '../models/api-result.models';
import { LoginRequest } from '../models/auth/login-request.model';
import { environment } from '../../../environments/environment';
import { PermissaoEnum } from '../enum/permissao.enum';
import { CadastroRequest } from '../models/auth/cadastro-request.model';
import { jwtDecode } from 'jwt-decode';
import { ToastrService } from 'ngx-toastr';
import { MixPanelService } from './mix-panel/mix-panel.service';
import { tap, switchMap, map, catchError, finalize, shareReplay } from 'rxjs/operators';
import { UsageLimitService } from './usage-limit/usage-limit.service';
import {
    isRedeAdsConnectProfilePath,
    REDE_ADS_INTEGRATION_LINK_TOKEN_STORAGE_KEY,
} from '../constants/rede-ads-connect.constants';
import { clearVideAlertDialogDismissedFlag } from '../components/dialogs/vide-alert-dialog/vide-alert-dialog.component';
import { isAllowedDevEmail } from '../utils/dev-access';
import { canSeeRatoeira as clienteCanSeeRatoeira } from '../guards/ratoeira/can-see-ratoeira';
import { ClientStorageKey, HubStorageKey, StorageService, TokenStorageKey } from './storage/storage.service';
import { buildHubLoginUrlWithReturn, resolveHubHomeUrl, resolveHubUrlAfterProductLogout } from '../utils/hub-redirect';
import { readHubTokenFromUrl, stripHubTokenFromUrl } from '../utils/hub-product-token';
import { isHubAccessToken } from '../utils/hub-auth-epoch';
import { setTermosAceitosLocal } from '../constants/termos-uso.constants';
import { MeOverview, NewMeClient } from '../models/auth/new-me.model';
import { FeatureEnum } from '../enum/feature.enum';

interface AccessTokenPayload {
    uid?: number | string;
    email?: string;
}

const ROCKSTAR_SESSION_KEY = 'dashboard.salesTracking.rockstarShown';

export type LogoutReason = 'user' | 'unauthorized';

const HELP_CENTER_URL = 'https://ratoeira-ads.gitbook.io/help-ratoeira';

@Injectable({ providedIn: 'root' })
export class AuthService {
    public urlBase = environment.urlApi;
    public cliente: NewMeClient | null = {};

    /** Emitido após hidratar nome e limites via GET /new-me. */
    readonly profileHydrated$ = new Subject<void>();
    private hydrateProfileRequest$: Observable<void> | null = null;
    private profileOutdated = false;
    private logoutInProgress = false;

    private static readonly HUB_BOOTSTRAP_GRACE_MS = 20_000;

    constructor(
        private httpClient: HttpClient,
        private navigationService: NavigationService,
        private toastrService: ToastrService,
        private mixPanelService: MixPanelService,
        private usageLimitService: UsageLimitService,
        private storage: StorageService,
    ) {
        this.applyTokenFromUrlIfPresent();
        this.isAtuthenticated();
    }

    /**
     * Aplica o accessToken do Hub (?hubToken=) e recria a sessão local do produto.
     */
    public applyHubTokenFromUrl(token: string): void {
        if (typeof window === 'undefined' || !token?.trim()) return;
        const trimmed = token.trim();
        if (isRedeAdsConnectProfilePath(window.location.pathname)) {
            sessionStorage.setItem(REDE_ADS_INTEGRATION_LINK_TOKEN_STORAGE_KEY, trimmed);
            return;
        }

        const previous = this.storage.getLocal(TokenStorageKey.ACCESS_TOKEN);
        const clienteIdChanged = this.hubTokenClienteIdChanged(previous, trimmed);
        const isNewSessionToken = previous !== trimmed;
        const enteredViaHubToken = this.storage.getSession(TokenStorageKey.FROM_QUERY) === '1';
        const isFreshSessionEntry = isNewSessionToken || enteredViaHubToken || clienteIdChanged;

        if (isFreshSessionEntry) {
            this.cliente = {};
            this.hydrateProfileRequest$ = null;
            this.profileOutdated = false;
            this.storage.removeSession(ClientStorageKey.TIMEZONE);
            sessionStorage.setItem(ROCKSTAR_SESSION_KEY, '0');
            clearVideAlertDialogDismissedFlag();
        }

        this.storage.setLocal(TokenStorageKey.ACCESS_TOKEN, trimmed);
        this.storage.setSession(TokenStorageKey.FROM_QUERY, '1');
        this.storage.setSession(HubStorageKey.BOOTSTRAP_AT, String(Date.now()));
        this.setupUserFromToken(trimmed);
    }

    /** Janela após ?hubToken=: evita logout em cascata enquanto a hidratação do perfil roda. */
    private isInHubBootstrapGrace(): boolean {
        if (this.storage.getSession(TokenStorageKey.FROM_QUERY) !== '1') {
            return false;
        }
        const raw = this.storage.getSession(HubStorageKey.BOOTSTRAP_AT);
        const startedAt = raw ? Number(raw) : Date.now();
        if (!raw) {
            this.storage.setSession(HubStorageKey.BOOTSTRAP_AT, String(startedAt));
        }
        return Date.now() - startedAt < AuthService.HUB_BOOTSTRAP_GRACE_MS;
    }

    /**
     * Se a URL tiver ?hubToken=, grava o accessToken e tira o parâmetro da barra.
     * Nas rotas Rede Ads o query `token` é de integração e não entra aqui.
     */
    private applyTokenFromUrlIfPresent(): void {
        if (typeof window === 'undefined') return;
        if (isRedeAdsConnectProfilePath(window.location.pathname)) {
            return;
        }
        const token = readHubTokenFromUrl(window.location.search);
        if (!token) return;

        this.applyHubTokenFromUrl(token);
        window.history.replaceState(
            {},
            '',
            stripHubTokenFromUrl(window.location.pathname, window.location.search, window.location.hash),
        );
    }

    private readSessionUserIdFromAccessToken(token: string): string | null {
        try {
            const decoded = jwtDecode<Record<string, unknown>>(token);
            const hubUserId = decoded['userId'] ?? decoded['hubUserId'] ?? decoded['uid'];
            if (hubUserId != null && String(hubUserId).trim() !== '') {
                return String(hubUserId);
            }
            return null;
        } catch {
            return null;
        }
    }

    private hubTokenClienteIdChanged(previous: string | null, next: string): boolean {
        if (!previous) {
            return false;
        }
        const prevId = this.readSessionUserIdFromAccessToken(previous);
        const nextId = this.readSessionUserIdFromAccessToken(next);
        if (prevId && nextId) {
            return prevId !== nextId;
        }
        return previous !== next;
    }

    public login(request: LoginRequest): Observable<ApiResult> {
        return this.httpClient.post<ApiResult>(this.urlBase + '/login', request).pipe(
            tap((result) => {
                if (result.success && result.data?.accessToken) {
                    this.storage.setLocal(TokenStorageKey.ACCESS_TOKEN, result.data.accessToken);
                    sessionStorage.setItem(ROCKSTAR_SESSION_KEY, '0');
                    clearVideAlertDialogDismissedFlag();
                    // Configura o usuário imediatamente para que this.cliente esteja disponível
                }
            }),
            switchMap((result) => {
                if (result.success && result.data?.accessToken) {
                    return forkJoin([this.usageLimitService.refreshUsage().pipe(catchError(() => of(null)))]).pipe(
                        map(() => result),
                    );
                }
                return of(result); // Retorna o resultado original caso não tenha sucesso ou token
            }),
            catchError((error) => {
                return throwError(() => error);
            }),
        );
    }

    public cadastro(request: CadastroRequest, rota = 'cadastro'): Observable<ApiResult> {
        return this.httpClient.post<ApiResult>(this.urlBase + '/' + rota, request);
    }

    /** Sessão aberta pelo hub-app-front (?hubToken= ou JWT com userId/hubUserId). */
    public isHubSession(): boolean {
        const accessToken = this.storage.getLocal(TokenStorageKey.ACCESS_TOKEN);
        if (!accessToken) {
            return this.storage.getSession(TokenStorageKey.FROM_QUERY) === '1';
        }

        return isHubAccessToken(accessToken) || this.storage.getSession(TokenStorageKey.FROM_QUERY) === '1';
    }

    private static readonly HUB_TOKEN_EXPIRY_BUFFER_MS = 30_000;

    /** Access token do Hub expirado ou inválido. */
    public isHubAccessTokenExpired(bufferMs = AuthService.HUB_TOKEN_EXPIRY_BUFFER_MS): boolean {
        if (!this.isHubSession()) {
            return false;
        }
        const accessToken = this.storage.getLocal(TokenStorageKey.ACCESS_TOKEN);
        if (!accessToken?.trim()) {
            return true;
        }
        try {
            const decoded = jwtDecode<{ exp?: number }>(accessToken);
            if (!decoded.exp) {
                return false;
            }
            return decoded.exp * 1000 <= Date.now() + bufferMs;
        } catch {
            return true;
        }
    }

    /**
     * Trata 401: sessão Hub ou ambiente com redirect Hub desloga e volta ao Hub (nunca login legado).
     * Requests com SKIP_LOGOUT_ON_401 só preservam a sessão em APIs externas (ex.: VTurb).
     */
    public handleUnauthorizedResponse(error: HttpErrorResponse, skipLogoutOn401 = false): void {
        if (error.status !== 401 || skipLogoutOn401) {
            return;
        }

        const debugDetails = {
            url: error.url,
            statusText: error.statusText,
            body: error.error,
            hubSession: this.isHubSession(),
            tokenExpired: this.isHubAccessTokenExpired(0),
            skipLogoutOn401,
        };
        console.error('[AuthService] HTTP 401', debugDetails);

        if (this.isInHubBootstrapGrace() && !this.isHubAccessTokenExpired(0)) {
            console.warn('[AuthService] 401 ignorado durante bootstrap do Hub (20s)', debugDetails);
            return;
        }

        if (this.isHubAccessTokenExpired(0)) {
            this.logout({ reason: 'unauthorized' });
            return;
        }

        if (this.isHubSession()) {
            if (this.isHubSessionAuthFailure(error)) {
                this.logout({ reason: 'unauthorized' });
                return;
            }
            if (skipLogoutOn401) {
                return;
            }
            return;
        }

        if (!skipLogoutOn401) {
            this.logout({ reason: 'unauthorized' });
        }
    }

    private isHubSessionAuthFailure(error: HttpErrorResponse): boolean {
        if (error.statusText === 'SSO token expired') {
            return false;
        }

        const apiBase = this.urlBase?.trim().replace(/\/$/, '') ?? '';
        const requestUrl = (error.url ?? '').split('?')[0].trim();
        const hitsMainApi = Boolean(apiBase) && requestUrl.startsWith(apiBase);

        if (!hitsMainApi) {
            return false;
        }

        const body = error.error as { message?: string } | null | undefined;
        const message = typeof body?.message === 'string' ? body.message : '';

        return (
            message.includes('Token SSO inválido ou expirado') ||
            message.includes('Sessão encerrada') ||
            message.includes('Sessão de impersonação encerrada') ||
            message.includes('Usuário não vinculado ao RAADS') ||
            message === 'Não Autorizado'
        );
    }

    private clearProductSession(): void {
        this.cliente = null;
        this.storage.removeLocal(TokenStorageKey.ACCESS_TOKEN);
        this.storage.removeSession(TokenStorageKey.FROM_QUERY);
        this.storage.removeSession(HubStorageKey.BOOTSTRAP_AT);
        this.storage.removeSession(ClientStorageKey.ACTIVE_ID);
        clearVideAlertDialogDismissedFlag();
    }

    logout(options?: { reason?: LogoutReason }) {
        if (this.logoutInProgress) {
            return;
        }

        const reason = options?.reason ?? 'user';
        const returnUrlBeforeClear = typeof window !== 'undefined' ? window.location.href : '';

        this.logoutInProgress = true;

        // Encerra só a sessão local do produto. A sessão do Hub continua no hub-app-front.
        this.clearProductSession();
        this.mixPanelService.reset();
        if (reason === 'user') {
            this.toastrService.success('Logout realizado com sucesso');
        }

        if (reason === 'unauthorized') {
            window.location.href = buildHubLoginUrlWithReturn(returnUrlBeforeClear || `${window.location.origin}/v2`);
            return;
        }

        const hubUrl = resolveHubUrlAfterProductLogout();
        window.location.href = hubUrl || buildHubLoginUrlWithReturn(`${window.location.origin}/v2`);
    }

    public isAtuthenticated() {
        const accessToken = this.storage.getLocal(TokenStorageKey.ACCESS_TOKEN);

        if (accessToken) {
            if (this.isHubSession() && this.isHubAccessTokenExpired(0)) {
                this.logout({ reason: 'unauthorized' });
                return false;
            }
            this.setupUserFromToken(accessToken);
            return true;
        } else {
            return false;
        }
    }

    /**
     * Decodifica o token e configura o usuário (sem carregar configs remotas)
     */
    private setupUserFromToken(accessToken: string) {
        if (!accessToken) return;

        const decoded = jwtDecode<AccessTokenPayload>(accessToken);
        const decodedRecord = decoded as Record<string, unknown>;
        const hubUserIdClaim = decodedRecord['userId'] ?? decodedRecord['hubUserId'];
        const uid = decoded.uid;
        const resolvedClienteId = isHubAccessToken(accessToken)
            ? hubUserIdClaim != null && String(hubUserIdClaim).trim() !== ''
                ? String(hubUserIdClaim)
                : null
            : uid != null && String(uid).trim() !== ''
              ? String(uid)
              : null;

        if (resolvedClienteId) {
            this.storage.setSession(ClientStorageKey.ACTIVE_ID, resolvedClienteId);
            if (this.cliente) {
                this.cliente.id = resolvedClienteId;
            }
        }

        const hubEmail =
            decodedRecord['userEmail'] != null
                ? String(decodedRecord['userEmail']).trim()
                : decodedRecord['email'] != null
                  ? String(decodedRecord['email']).trim()
                  : '';
        if (hubEmail && this.cliente && !this.cliente.email?.trim()) {
            this.cliente.email = hubEmail.toLowerCase();
            if (typeof this.cliente.name !== 'string' || !this.cliente.name.trim()) {
                this.cliente.name = hubEmail.split('@')[0] || hubEmail;
            }
        }

        this.mixPanelService.identify(String(this.cliente?.id));
        if (this.needsProfileHydration()) {
            this.ensureProfileHydrated().subscribe();
        }
    }

    public markProfileOutdated(): void {
        this.profileOutdated = true;
    }

    /** Perfil + limites via GET /new-me. */
    public ensureProfileHydrated(): Observable<void> {
        if (!this.profileOutdated && !this.needsProfileHydration()) {
            return of(undefined);
        }
        return this.fetchClienteMeProfile();
    }

    private fetchClienteMeProfile(): Observable<void> {
        if (this.hydrateProfileRequest$) {
            return this.hydrateProfileRequest$;
        }

        this.hydrateProfileRequest$ = this.usageLimitService.refreshUsage().pipe(
            tap((result) => {
                if (result.success) {
                    this.profileOutdated = false;
                    const overview = this.usageLimitService.getLastNewMeOverview();
                    if (overview) {
                        this.applyProfileFromNewMe(overview);
                    }
                    this.storage.removeSession(TokenStorageKey.FROM_QUERY);
                }
                this.profileHydrated$.next();
            }),
            map(() => undefined),
            catchError(() => {
                this.profileHydrated$.next();
                return of(undefined);
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
            finalize(() => {
                this.hydrateProfileRequest$ = null;
            }),
        );

        return this.hydrateProfileRequest$;
    }

    /** Guarda o client do /new-me como perfil da sessão. */
    private applyProfileFromNewMe(overview: MeOverview): void {
        const source = overview.client;
        if (!source) {
            return;
        }

        const sessionClienteId =
            this.resolveExpectedClienteIdFromToken() ??
            this.storage.getSession(ClientStorageKey.ACTIVE_ID) ??
            (this.cliente?.id ? String(this.cliente.id) : null);
        const accountExternalId =
            overview.account?.externalClientId != null ? String(overview.account.externalClientId) : null;

        const client: NewMeClient = { ...source };
        // client.id do Hub é UUID. A sessão usa o id RAADS do token.
        if (sessionClienteId) {
            client.id = sessionClienteId;
        } else if (accountExternalId) {
            client.id = accountExternalId;
        }

        const email = this.readProfileEmail(client.email, overview.account?.subscriberEmail);
        if (email) {
            client.email = email;
        }
        if (typeof client.name !== 'string' || !client.name.trim()) {
            if (email) {
                client.name = email.split('@')[0];
            }
        } else {
            client.name = client.name.trim();
        }

        if (client.aceitou_termos_de_uso != null) {
            client.aceitou_termos_de_uso = Number(client.aceitou_termos_de_uso);
            if (client.aceitou_termos_de_uso === 1 && client.id) {
                setTermosAceitosLocal(String(client.id));
            }
        }

        this.cliente = client;
        this.redirectClientAwayFromDev();
    }

    private readProfileEmail(clientEmail: unknown, accountEmail: unknown): string {
        const email = typeof clientEmail === 'string' && clientEmail.trim() ? clientEmail : accountEmail;
        return typeof email === 'string' ? email.trim().toLowerCase() : '';
    }

    private needsProfileHydration(): boolean {
        const accessToken = this.storage.getLocal(TokenStorageKey.ACCESS_TOKEN);
        if (!accessToken) {
            return false;
        }
        try {
            if (this.cliente != null && this.cliente.rede_ads_ativas === undefined) {
                return true;
            }

            if (this.storage.getSession(TokenStorageKey.FROM_QUERY) === '1') {
                return true;
            }

            const name = typeof this.cliente?.name === 'string' ? this.cliente.name.trim() : '';
            const hasIdentity = Boolean(name || this.cliente?.email?.trim());
            const hasLimits = this.usageLimitService.getCurrentUsage().length > 0;
            return !(hasIdentity && hasLimits);
        } catch {
            return false;
        }
    }

    private resolveExpectedClienteIdFromToken(): string | null {
        const accessToken = this.storage.getLocal(TokenStorageKey.ACCESS_TOKEN);
        if (!accessToken) {
            return null;
        }
        try {
            const decoded = jwtDecode<Record<string, unknown>>(accessToken);
            if (isHubAccessToken(accessToken)) {
                const hubUserId = decoded['userId'] ?? decoded['hubUserId'];
                if (hubUserId != null && String(hubUserId).trim() !== '') {
                    return String(hubUserId);
                }
                return null;
            }
            if (decoded['uid'] != null && String(decoded['uid']).trim() !== '') {
                return String(decoded['uid']);
            }
            return null;
        } catch {
            return null;
        }
    }

    private redirectClientAwayFromDev(): void {
        if (environment.production) {
            return;
        }
        if (this.verificaAdminPermissao() || this.getImpersonationInfo().isImpersonating) {
            return;
        }
        if (isAllowedDevEmail(this.cliente?.email)) {
            return;
        }
        window.location.replace('https://app.ratoeiraads.com.br');
    }

    public getPermissoes(): PermissaoEnum[] {
        const raw = this.cliente?.['permissao'];
        if (raw == null) {
            return [];
        }

        const values = Array.isArray(raw) ? raw.map(String) : String(raw).split(',');
        return values
            .map((permission) => permission.trim())
            .filter((permission): permission is PermissaoEnum =>
                Object.values(PermissaoEnum).includes(permission as PermissaoEnum),
            );
    }

    public verificaAdminPermissao(): boolean {
        const permissao = this.getPermissoes();
        return (
            permissao.includes(PermissaoEnum.Admin) ||
            permissao.includes(PermissaoEnum.Suporte) ||
            permissao.includes(PermissaoEnum.Dev)
        );
    }

    public canAccessFontesTrafego(): boolean {
        return this.getLimitedRedeAdsTotal() != null;
    }

    public canSeeRatoeira(): boolean {
        return clienteCanSeeRatoeira(this.cliente?.id);
    }

    public needsForcedRedeAdsSelection(): boolean {
        const total = this.getLimitedRedeAdsTotal();
        const ativas = this.cliente?.rede_ads_ativas;
        if (total == null || ativas === undefined) {
            return false;
        }

        if (ativas == null) {
            return true;
        }

        return ativas.length > total;
    }

    public needsUnlimitedRedeAdsReset(): boolean {
        const limit = this.usageLimitService.getLimit(FeatureEnum.TOTAL_REDE_ADS);
        return limit?.ilimitado === true && this.cliente?.rede_ads_ativas != null;
    }

    private getLimitedRedeAdsTotal(): number | null {
        const limit = this.usageLimitService.getLimit(FeatureEnum.TOTAL_REDE_ADS);
        const monthly = limit?.limite_mensal;
        if (limit == null || limit.ilimitado === true || typeof monthly !== 'number' || monthly <= 0) {
            return null;
        }

        return monthly;
    }

    public isRedeAdsAtiva(redeAdsId?: number | null): boolean {
        const ativas = this.cliente?.rede_ads_ativas;
        if (ativas == null) {
            return true;
        }

        if (redeAdsId == null) {
            return ativas.length > 0;
        }

        return ativas.map(Number).includes(Number(redeAdsId));
    }

    public getHelpCenterUrl(): string {
        return HELP_CENTER_URL;
    }

    public goToSystemVersion() {
        this.navigationService.goTo('/v2');
    }

    public habilitarTransloco() {
        return this.verificaAdminPermissao();
    }

    public getStaffId(): string | null {
        try {
            const token = this.storage.getLocal(TokenStorageKey.ACCESS_TOKEN);
            const id = token ? jwtDecode<{ staff?: { id?: unknown } }>(token).staff?.id : null;
            if (typeof id === 'number' && Number.isFinite(id)) {
                return String(id);
            }
            return typeof id === 'string' && id.trim() ? id.trim() : null;
        } catch {
            return null;
        }
    }

    public getImpersonationInfo(): { isImpersonating: boolean; subject: string | null } {
        const accessToken = this.storage.getLocal(TokenStorageKey.ACCESS_TOKEN);
        if (!accessToken) {
            return { isImpersonating: false, subject: null };
        }
        try {
            const decoded = jwtDecode<Record<string, unknown>>(accessToken);
            const staff = decoded['staff'];
            if (!staff || typeof staff !== 'object') {
                return { isImpersonating: false, subject: null };
            }
            const subject =
                (typeof decoded['userEmail'] === 'string' && decoded['userEmail'].trim()) ||
                (typeof decoded['email'] === 'string' && decoded['email'].trim()) ||
                null;
            return { isImpersonating: true, subject };
        } catch {
            return { isImpersonating: false, subject: null };
        }
    }

    /** Encerra a sessão local e devolve o usuário ao Hub, onde a impersonação é encerrada. */
    public stopImpersonation(): Promise<boolean> {
        const hubUrl = resolveHubHomeUrl();
        if (typeof window === 'undefined' || !hubUrl) {
            return Promise.resolve(false);
        }
        this.clearProductSession();
        window.location.href = hubUrl;
        return Promise.resolve(true);
    }
}
